import { IconChainLink4 } from 'central-icons/IconChainLink4';
import { useEffect, useRef, useState } from 'react';

import { Button, Dialog, Input, Link, MenuItem, Text } from '@revelio/ui';

import { copyTextToClipboard } from '../../../../shared/selection-lists/components/SelectionListSelect/utils';
import { CheckIcon } from '../../../../shared/ui/icons';
import { SharedReportRateLimitError } from '../../api/dash-meta-rate-limit-error';
import { createSharedReport } from '../../api/shared-reports-api';
import { useToast } from '../../hooks/useToast';
import { useOptionalReportDocument } from '../../report-document/report-document-context';
import { usePageConfig } from '../PageConfigProvider/PageConfigProvider';
import { usePageState } from '../PageState/PageStateProvider';
import { useCoverSelectedParameters } from '../Reports/useCoverSelectedParameters';
import { SHARED_FLOW_LAYOUT_MARKER } from './SharedFlowReport';
import { useSharedReportData } from './SharedReportDataContext';
import styles from './share-report-modal.module.css';

interface ShareReportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ShareReportModal = ({
  isOpen,
  onClose,
}: ShareReportModalProps) => {
  const { config } = usePageConfig();
  const { pageState } = usePageState();
  const sharedReportData = useSharedReportData();
  const coverParameters = useCoverSelectedParameters();
  const isFlowReport = useOptionalReportDocument() !== null;
  const toast = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isOpen) {
      setShareUrl('');
      setCopied(false);
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const handleGenerateLink = async () => {
    if (!sharedReportData) return;

    setIsLoading(true);
    try {
      const chartData = sharedReportData.getAllChartData();

      const snapshotConfig = JSON.parse(JSON.stringify(config));
      // Bake resolved cover page parameters
      snapshotConfig._resolvedCoverParameters = coverParameters;
      if (isFlowReport) {
        snapshotConfig._layout = SHARED_FLOW_LAYOUT_MARKER;
      }
      for (const tab of snapshotConfig.tabs ?? []) {
        for (const item of tab.tabContent?.items ?? []) {
          if (!item.plot) continue;

          const registered = chartData[item.id];
          const markdown = registered?.markdown;
          const hasReplotComponent = Boolean(registered?.component);
          const isTextChart = item.plot.chartType === 'text';

          if (hasReplotComponent) {
            // Replots HOC registered component + props; viewer renders live
            // via StaticChartRenderer. Nothing to bake into plot config.
          } else if (markdown != null) {
            // Text / AI-summary charts register their resolved markdown so the
            // viewer renders via MarkdownRenderer without any data fetching.
            if (!isTextChart) {
              item.plot._originalChartType = item.plot.chartType;
              item.plot.chartType = 'text';
            }
            item.plot.textHtml = markdown;
          }

          if (registered?.title != null) {
            item.title = registered.title;
          }
        }
      }

      const { token } = await createSharedReport({
        name: config.name,
        pageConfig: snapshotConfig as unknown as Record<string, unknown>,
        chartData: chartData as unknown as Record<string, unknown>,
        pageState: pageState as unknown as Record<string, unknown>,
      });

      setShareUrl(`${window.location.origin}/shared/${token}`);
    } catch (error) {
      const isRateLimited = error instanceof SharedReportRateLimitError;
      toast({
        position: 'top-right',
        title: isRateLimited
          ? 'Too many share links generated'
          : 'Failed to generate share link',
        description: isRateLimited
          ? 'Please try again later.'
          : 'Please try again.',
        status: isRateLimited ? 'warning' : 'error',
        duration: isRateLimited ? 5000 : 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    const didCopy = await copyTextToClipboard(shareUrl);
    if (!didCopy) return;

    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
    }
    setCopied(true);
    copyTimeoutRef.current = setTimeout(() => {
      setCopied(false);
      copyTimeoutRef.current = null;
    }, 2000);
  };

  return (
    <Dialog open={isOpen} onClose={onClose} title="Share report">
      <div className={styles.body}>
        <div className={styles.linkField}>
          <Text fontSize="sm">
            Anyone with this link can view a snapshot of this report
          </Text>
          <Input
            value={shareUrl}
            placeholder="No shared link"
            disabled={!shareUrl}
            readOnly
            endSlot={
              shareUrl ? (
                <Button
                  className={styles.copyBtn}
                  size="sm"
                  color="primary"
                  onClick={() => void handleCopy()}
                >
                  {copied ? (
                    <>
                      <CheckIcon className={styles.copiedIcon} /> Copied
                    </>
                  ) : (
                    'Copy Link'
                  )}
                </Button>
              ) : (
                <Button
                  className={styles.copyBtn}
                  size="sm"
                  color="primary"
                  loading={isLoading}
                  onClick={handleGenerateLink}
                >
                  Generate Link
                </Button>
              )
            }
          />
          {shareUrl && (
            <div className={styles.linkAction}>
              <Link
                underline="rest"
                className={styles.previewLink}
                href={shareUrl}
                target="_blank"
                rel="noreferrer"
              >
                Preview link in new tab
              </Link>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};

// The Radix menu unmounts its content (this component) when the menu closes,
// so the modal cannot live here — its open state would unmount with the menu.
// The parent owns the modal and passes the opener down.
export const ShareReportMenuItem = ({ onOpen }: { onOpen: () => void }) => (
  <MenuItem onClick={onOpen} icon={<IconChainLink4 size={16} />} title="Share">
    Share
  </MenuItem>
);
